import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { compressImage } from "../lib/imageCompression";
import What3WordsLink from "../components/What3WordsLink";
import PropertyAccessCard from "../components/PropertyAccessCard";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Phone, Clock, CheckCircle, CurrencyGbp, Receipt,
  Upload, Trash, Warning, Compass, FileText, ClipboardText, Image as ImageIcon, Paperclip
} from "@phosphor-icons/react";
import { ImageLightbox } from "../components/ImageLightbox";
import AuthImage from "../components/AuthImage";
import { openAuthFile } from "../lib/fileOpen";
import JobChecklist, { checklistStats } from "../components/JobChecklist";
import offlinePhotoQueue from "../lib/offlinePhotoQueue";
import EntityFiles from "../components/EntityFiles";
import { JobPartsPanel } from "../components/stock/JobPartsPanel";
import JobFormFillDialog from "../components/JobFormFillDialog";
import JobCompliancePanel from "../components/JobCompliancePanel";
import { NoticeBox } from "../components/NoticeBox";
import RunningLateButton from "../components/RunningLateButton";
import OnMyWayButton from "../components/OnMyWayButton";
import { ScopeText } from "../components/jobs/ScopeOfWorksField";
import MakeSafeChecklist from "../components/jobs/MakeSafeChecklist";
import JobSafetyPanel from "../components/hs/JobSafetyPanel";
import BeforeAfterCompare from "../components/jobs/BeforeAfterCompare";
import VoiceNoteRecorder from "../components/VoiceNoteRecorder";
import { Microphone, Car } from "@phosphor-icons/react";
import { PopsJobCard } from "../components/pops/PopsJobCard";

function fmtAddress(j) {
  return [j?.street_address, j?.town_city, j?.postcode].filter(Boolean).join(", ");
}

function mapsUrl(j) {
  if (j?.latitude && j?.longitude) {
    return `https://www.google.com/maps/dir/?api=1&destination=${j.latitude},${j.longitude}`;
  }
  const addr = fmtAddress(j);
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
}

/**
 * Route params:
 *  - :kind = "jobs" | "emergency"
 *  - :id   = job id
 */
export default function InHouseJobDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const entityType = location.pathname.includes("/emergency/") ? "emergency_job" : "job";
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [completeDlg, setCompleteDlg] = useState({ open: false, notes: "" });
  const [expenseDlg, setExpenseDlg] = useState({ open: false, amount: "", description: "", category: "materials", file: null });
  const [expenses, setExpenses] = useState([]);
  const [expensesTotal, setExpensesTotal] = useState(0);
  const [lightbox, setLightbox] = useState({ open: false, idx: 0 });
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [fillForm, setFillForm] = useState(null);
  const fileInputRef = useRef(null);
  const [proximity, setProximity] = useState({ nearby: false, distance_m: null });
  // Compliance ack readiness — published by JobCompliancePanel via onStatusChange.
  const [compliance, setCompliance] = useState({ loaded: false, total: 0, ackedCount: 0, allAcked: false });
  // H&S readiness (permits accepted, point-of-work risk assessment) + AI photo-QA hand-back — published by JobSafetyPanel.
  const [safety, setSafety] = useState({ loaded: false, blocking: [], ramsBlocking: [], qaReturned: false, photoQa: null });
  const [qaAck, setQaAck] = useState({ checked: false, note: "" });
  const [voiceNotes, setVoiceNotes] = useState([]);

  // Supplier operatives / supplier owners use the same field view — expenses are an in-house-only concept.
  const isSupplierSide = ["vendor", "vendor_operative"].includes(user?.role);
  const backPath = user?.role === "vendor" ? `/supplier-portal/${entityType === "emergency_job" ? "emergency" : "jobs"}/${id}` : "/my-work";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [jRes, exRes] = await Promise.all([
        API.get(`/in-house-portal/${entityType}/${id}`),
        isSupplierSide ? Promise.resolve({ data: { expenses: [], total: 0 } }) : API.get(`/in-house-portal/${entityType}/${id}/expenses`),
      ]);
      setJob(jRes.data);
      setVoiceNotes([...(jRes.data.voice_notes || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
      setExpenses(exRes.data.expenses || []);
      setExpensesTotal(exRes.data.total || 0);

      // Job sheets attach to every job type (jobs + emergencies) — /jobs/{id}/form-submissions resolves either.
      const linked = jRes.data.job_form_ids || [];
      if (linked.length) {
        const [tplsRes, subsRes] = await Promise.all([
          API.get(`/job-forms`).catch(() => ({ data: { forms: [] } })),
          API.get(`/jobs/${id}/form-submissions`).catch(() => ({ data: { submissions: [] } })),
        ]);
        setForms((tplsRes.data.forms || []).filter(f => linked.includes(f.id)));
        setSubmissions(subsRes.data.submissions || []);
      } else {
        setForms([]); setSubmissions([]);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load this job");
      if (e.response?.status === 403 || e.response?.status === 404) {
        navigate(backPath);
      }
    } finally {
      setLoading(false);
    }
  }, [entityType, id, navigate, isSupplierSide, backPath]);

  useEffect(() => { load(); }, [load]);

  // Haversine distance in metres
  function _distanceM(lat1, lng1, lat2, lng2) {
    if ([lat1, lng1, lat2, lng2].some((v) => v == null || isNaN(v))) return null;
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
              Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  // Geo-fenced auto check-in prompt: only while NOT arrived, and only if the job has coords
  useEffect(() => {
    if (!job || job.my_attendance?.arrived_at || job.status === "completed") return;
    if (job.latitude == null || job.longitude == null) return;
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const d = _distanceM(pos.coords.latitude, pos.coords.longitude, job.latitude, job.longitude);
        if (d == null) return;
        setProximity({ nearby: d <= 500, distance_m: Math.round(d) });
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
    );
    return () => { try { navigator.geolocation.clearWatch(watchId); } catch {} };
  }, [job]);

  const getLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ latitude: null, longitude: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve({ latitude: null, longitude: null }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });

  const markArrived = async () => {
    if (!complianceReady) {
      toast.error("Acknowledge the required compliance documents first.");
      return;
    }
    if (ramsBlock) {
      toast.error(`${ramsBlock}.`);
      return;
    }
    setWorking(true);
    try {
      const coords = await getLocation();
      const resp = await API.post(`/in-house-portal/${entityType}/${id}/arrive`, coords);
      if (resp?.data?.queued) {
        toast.info("You're offline — arrival saved and will sync automatically.");
      } else if (coords.latitude == null) {
        toast.warning("Arrival logged but location was unavailable — allow location access for accurate tracking.");
      } else {
        toast.success("On-site time captured");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not mark arrived");
    } finally {
      setWorking(false);
    }
  };

  const markComplete = async () => {
    setWorking(true);
    try {
      const coords = await getLocation();
      const payload = { ...coords, notes: completeDlg.notes, ...(safety.qaReturned && qaAck.checked ? { qa_acknowledged: true, qa_note: qaAck.note } : {}) };
      const pendingPhotos = await offlinePhotoQueue.hasPendingForEntity(entityType, id).catch(() => false);
      if (!navigator.onLine || pendingPhotos) {
        // Offline (or photos still queued): store the completion — it fires
        // automatically right after the queued photos finish uploading.
        await offlinePhotoQueue.queueCompletion(entityType, id, payload);
        toast.info("Completion saved offline — it will submit automatically once your photos sync.");
        setCompleteDlg({ open: false, notes: "" });
        return;
      }
      await API.post(`/in-house-portal/${entityType}/${id}/complete`, payload);
      toast.success("Job completed");
      setCompleteDlg({ open: false, notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not complete");
    } finally {
      setWorking(false);
    }
  };

  const submitExpense = async () => {
    const amt = parseFloat(expenseDlg.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setWorking(true);
    try {
      const fd = new FormData();
      fd.append("amount", String(amt));
      fd.append("description", expenseDlg.description);
      fd.append("category", expenseDlg.category);
      if (expenseDlg.file) {
        const receipt = await compressImage(expenseDlg.file);
        fd.append("receipt", receipt);
      }
      await API.post(`/in-house-portal/${entityType}/${id}/expenses`, fd);
      toast.success("Expense submitted");
      setExpenseDlg({ open: false, amount: "", description: "", category: "materials", file: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit expense");
    } finally {
      setWorking(false);
    }
  };

  const deleteExpense = async (xId) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await API.delete(`/in-house-portal/expenses/${xId}`);
      toast.success("Expense deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  if (!job) return null;

  // Crew-aware: "arrived" means *this* operative has checked in (each crew member checks in separately)
  const myArrivedAt = job.my_attendance?.arrived_at || null;
  const arrived = !!myArrivedAt;
  const completed = job.status === "completed";
  const crew = job.crew || [];
  // Compliance acknowledgement gate — operatives can't "Arrive on site" or
  // "Complete job" until they've ticked every linked RAMS / Method Statement.
  // `total === 0` means the job has no compliance docs → no gating applied.
  const complianceReady = compliance.loaded && (compliance.total === 0 || compliance.allAcked);
  const safetyBlock = safety.blocking[0] || "";
  // RAMS arrival gate — reason text from the safety panel (unsigned RAMS, or a high-risk job with no signed RAMS yet)
  const ramsBlock = safety.ramsGate?.blocked ? safety.ramsGate.reason : "";

  return (
    <div className="pb-6" data-testid="in-house-job-detail">
      {/* Sticky mobile-friendly header — primary back nav for PWA */}
      <div className="sticky top-0 z-30 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 border-b border-border" data-testid="in-house-job-header">
        <div className="max-w-4xl mx-auto px-3 py-2.5 flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(backPath)}
            className="flex items-center gap-1 px-2 py-2 -ml-2 rounded-md hover:bg-muted active:bg-muted/70 text-sm font-medium text-slate-700 dark:text-slate-200 min-h-[44px] min-w-[44px]"
            data-testid="in-house-job-back-btn"
            aria-label="Back to my work"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="hidden sm:inline">{user?.role === "vendor" ? "Job" : "My Work"}</span>
          </button>
          <h1 className="text-sm font-semibold truncate flex-1 text-center sm:text-left" data-testid="in-house-job-header-title">{job.title}</h1>
          {entityType === "emergency_job" && (
            <span className="text-[10px] px-2 py-1 rounded bg-red-100 text-red-700 uppercase font-semibold shrink-0">
              <Warning className="inline h-3 w-3 mr-0.5" /> SOS
            </span>
          )}
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-4 max-w-4xl mx-auto">
        {/* Summary */}
      <div className="bg-card border border-border rounded-lg p-5">
        <h1 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="in-house-job-title">{job.title}</h1>
        {job.parent_job_reference && (
          <div className="mt-1.5 space-y-1.5" data-testid="in-house-sub-job-chip">
            <p className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/40 rounded-full px-2 py-0.5">
              Item {job.sub_job_index || ""}{job.sub_job_sequence?.length ? ` of ${job.sub_job_sequence.length}` : ""} at this property{job.parent_job_title ? ` — ${job.parent_job_title}` : ""}
            </p>
            {job.sub_job_sequence?.length > 1 && (
              <ol className="flex flex-wrap items-center gap-1 text-[11px]" data-testid="in-house-sub-job-sequence">
                {job.sub_job_sequence.map((s, i) => {
                  const done = s.status === "completed" || s.status === "cancelled";
                  return (
                    <li key={s.id} className="flex items-center gap-1">
                      {i > 0 && <span className="text-slate-400">→</span>}
                      <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 ${s.is_me ? "bg-violet-600 text-white font-semibold" : done ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 line-through decoration-emerald-500/60" : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`} data-testid={`in-house-sequence-item-${s.sub_job_index}`}>
                        {s.sub_job_index}. {s.category && s.category !== "general" ? s.category.replace(/_/g, " ") : s.title}{s.is_me ? " (you)" : done ? " ✓" : ""}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        )}

        {job.arrival_off_site && (
          <div className="mt-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 rounded-md p-3 flex items-start gap-2" data-testid="in-house-off-site-banner">
            <Warning size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Off-site check-in flagged</p>
              <p className="text-xs text-amber-800 dark:text-amber-300">
                You were {job.arrival_distance_m}m from the registered job location when you checked in. Your office has been notified.
              </p>
            </div>
          </div>
        )}

        <div className="mt-3 space-y-2 text-sm">
          <p className="flex items-start gap-2 text-slate-700 dark:text-slate-300">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{fmtAddress(job) || job.property_name || "Address not set"}</span>
          </p>
          {job.what3words && <div className="pl-6"><What3WordsLink words={job.what3words} /></div>}
          {job.site_contact_name && (
            <p className="flex items-center gap-2 text-slate-700 dark:text-slate-300" data-testid="in-house-site-contact">
              <span className="text-xs uppercase tracking-wide text-slate-500">Site contact</span>
              <span className="font-medium">{job.site_contact_name}</span>
              {job.site_contact_phone && (
                <a href={`tel:${job.site_contact_phone}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                  <Phone className="h-3.5 w-3.5" /> {job.site_contact_phone}
                </a>
              )}
            </p>
          )}
          <PropertyAccessCard entityType={entityType} entityId={id} compact />
          {job.description && (
            <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap pt-2 border-t border-border mt-2">
              {job.description}
            </p>
          )}
        </div>

        {/* Proximity auto-check-in prompt */}
        {!arrived && !completed && proximity.nearby && complianceReady && !ramsBlock && (
          <button onClick={markArrived} disabled={working}
                  className="mt-5 w-full flex items-center gap-3 p-4 rounded-lg border-2 border-green-400 bg-green-50 dark:bg-green-950/30 hover:bg-green-100 dark:hover:bg-green-950/50 transition text-left animate-pulse"
                  data-testid="in-house-proximity-prompt">
            <div className="h-10 w-10 rounded-full bg-green-500 text-white flex items-center justify-center shrink-0">
              <MapPin className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-green-900 dark:text-green-200">You're {proximity.distance_m}m from the job</p>
              <p className="text-sm text-green-700 dark:text-green-300">Tap to check in now</p>
            </div>
            <CheckCircle className="h-6 w-6 text-green-600" />
          </button>
        )}

        {/* Action buttons */}
        <div className="mt-5 flex flex-wrap gap-2">
          <a href={mapsUrl(job)} target="_blank" rel="noreferrer">
            <Button variant="outline" data-testid="in-house-directions-btn">
              <Compass className="h-4 w-4 mr-2" /> Get directions
            </Button>
          </a>
          {!isSupplierSide && (
            <Button variant="outline" onClick={() => navigate(`/my-work/mileage?job=${entityType === "emergency_job" ? "emergency" : "job"}:${id}`)} data-testid="in-house-log-mileage-btn" title="Log the drive to this job — the address, purpose and an estimate of the miles are filled in for you">
              <Car className="h-4 w-4 mr-2" /> Log mileage
            </Button>
          )}
          {!arrived && !completed && (
            <Button
              onClick={markArrived}
              disabled={working || !complianceReady || !!ramsBlock}
              data-testid="in-house-arrive-btn"
              title={!complianceReady ? "Acknowledge the required compliance documents first" : ramsBlock || ""}
            >
              <MapPin className="h-4 w-4 mr-2" /> {working ? "Capturing location…" : "Arrive on site"}
            </Button>
          )}
          {!arrived && !completed && (
            <OnMyWayButton entityType={entityType} entityId={id} current={job.my_attendance}
              onSaved={(d) => setJob((j) => ({ ...j, my_attendance: { ...(j.my_attendance || {}), en_route_at: d.en_route_at, eta_time: d.eta_time || j.my_attendance?.eta_time } }))} />
          )}
          {!arrived && !completed && (
            <RunningLateButton entityType={entityType} entityId={id} current={job.my_attendance}
              onSaved={(d) => setJob((j) => ({ ...j, my_attendance: { ...(j.my_attendance || {}), eta_at: d.eta_at, eta_time: d.eta_time, eta_note: d.note },
                crew: (j.crew || []).map((c) => (c.user_id === user?._id ? { ...c, eta_at: d.eta_at, eta_time: d.eta_time } : c)) }))} />
          )}
          {arrived && !completed && (
            <Button
              onClick={() => {
                if (!complianceReady) { toast.error("Acknowledge the required compliance documents first."); return; }
                if (safetyBlock) { toast.error(`${safetyBlock} first.`); return; }
                setCompleteDlg({ open: true, notes: "" });
              }}
              disabled={!complianceReady || !!safetyBlock}
              data-testid="in-house-complete-btn"
              title={!complianceReady ? "Acknowledge the required compliance documents first" : safetyBlock ? `${safetyBlock} first` : ""}
            >
              <CheckCircle className="h-4 w-4 mr-2" /> Complete job
            </Button>
          )}
          {completed && (
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded bg-green-100 text-green-800 text-sm font-medium">
              <CheckCircle className="h-4 w-4" /> Completed
            </span>
          )}
        </div>

        {/* Time on site */}
        {(arrived || completed) && (
          <div className="mt-4 text-xs text-slate-500 space-y-0.5 border-t border-border pt-3">
            {arrived && <p data-testid="in-house-my-arrival">You arrived: {new Date(myArrivedAt).toLocaleString()} {job.my_attendance?.latitude ? `· (${Number(job.my_attendance.latitude).toFixed(5)}, ${Number(job.my_attendance.longitude).toFixed(5)})` : ""}</p>}
            {completed && <p>Completed: {new Date(job.completed_at).toLocaleString()} {(job.my_attendance?.minutes ?? job.on_site_minutes) != null ? `· ${job.my_attendance?.minutes ?? job.on_site_minutes} min on site` : ""}</p>}
          </div>
        )}

        {/* Crew — who else is on this job and whether they've checked in */}
        {crew.length > 1 && (
          <div className="mt-4 border-t border-border pt-3" data-testid="in-house-crew-panel">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Crew · {crew.length} operatives</p>
            <div className="flex flex-wrap gap-1.5">
              {crew.map((c, i) => (
                <span key={c.user_id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${c.arrived_at ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300" : "border-border bg-muted/40 text-muted-foreground"}`} data-testid={`in-house-crew-member-${c.user_id}`}>
                  {i === 0 && <span title="Lead">★</span>}{c.name || "Operative"}{c.user_id === user?._id ? " (you)" : ""} · {c.arrived_at ? `on site ${new Date(c.arrived_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : c.eta_time ? `running late · ETA ${c.eta_time}` : "not arrived"}
                </span>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">Everyone presses their own Arrive. Any one of you can Complete the job for the whole crew.</p>
          </div>
        )}
      </div>

      {/* Required compliance — operative must acknowledge before "Arrive on site" */}
      <JobCompliancePanel jobId={id} mode="ack" onStatusChange={setCompliance} />

      {/* POPs waste (bespoke module) — crews record what goes on the van */}
      {entityType === "job" && <PopsJobCard jobId={id} user={user} />}

      {/* Permits to work, point-of-work risk assessment, AI photo-QA hand-back, lone-worker timer */}
      <JobSafetyPanel entityType={entityType} entityId={id} arrived={arrived} completed={completed} onStatusChange={setSafety} onChanged={load} />
      {safetyBlock && !completed && <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="in-house-safety-block-hint">Before you can complete: {safety.blocking.join(" · ")}.</p>}

      <NoticeBox notice={job.notice} testid="in-house-notice" />

      {/* Emergency: make-safe + repair bullets become photo-confirmed tick boxes; the scope text card only shows when there are none */}
      {entityType === "emergency_job" && (job.make_safe?.total > 0 || job.make_safe?.repair?.total > 0) && (
        <MakeSafeChecklist jobId={id} makeSafe={job.make_safe} readOnly={isSupplierSide} testid="in-house-make-safe"
          onChange={(ms) => setJob((j) => ({ ...j, make_safe: ms, made_safe_at: ms.made_safe_at, repairs_done_at: ms.repair?.completed_at }))} />
      )}

      {/* Scope of Works (client-facing scope) + Instructions (internal notes for the crew) */}
      {job.scope_of_works && (entityType !== "emergency_job" || !(job.make_safe?.total > 0 || job.make_safe?.repair?.total > 0)) && (
        <div className="bg-card border border-border rounded-lg p-5 border-l-4 border-l-primary" data-testid="in-house-scope-of-works">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-2">
            <ClipboardText className="h-5 w-5" /> Scope of Works
          </h2>
          <ScopeText text={job.scope_of_works} className="text-slate-700 dark:text-slate-300 leading-relaxed" />
        </div>
      )}
      {job.instructions && (
        <div className="bg-card border border-border rounded-lg p-5" data-testid="in-house-instructions">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-2">
            <ClipboardText className="h-5 w-5" /> {job.scope_of_works ? "Instructions" : "Scope of Works"}
          </h2>
          <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
            {job.instructions}
          </p>
        </div>
      )}

      {/* Attachments gallery (photos on the job record) */}
      {Array.isArray(job.attachments) && job.attachments.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-5" data-testid="in-house-attachments">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <ImageIcon className="h-5 w-5" /> Job Images & Attachments
              <span className="text-xs text-muted-foreground font-normal">({job.attachments.length})</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {job.attachments.map((att, i) => {
              const isImage = (att.mime || att.type || "").startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(att.filename || att.name || att.url || "");
              if (!isImage) {
                return (
                  <button key={i} type="button" onClick={() => openAuthFile(att.url)}
                     className="block w-full border border-border rounded-md p-3 hover:bg-muted/50 transition"
                     data-testid={`in-house-attachment-file-${i}`}>
                    <Paperclip className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                    <p className="text-[11px] truncate text-center">{att.filename || att.name || "File"}</p>
                  </button>
                );
              }
              return (
                <button key={i} onClick={() => setLightbox({ open: true, idx: i })}
                        className="relative aspect-square rounded-md overflow-hidden border border-border hover:opacity-80 transition"
                        data-testid={`in-house-attachment-image-${i}`}>
                  <AuthImage path={att.url} alt={att.filename || "Attachment"} className="w-full h-full object-cover" />
                </button>
              );
            })}
          </div>
          <ImageLightbox
            images={job.attachments.filter(a => (a.mime || a.type || "").startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(a.filename || a.name || a.url || "")).map(a => ({ url: a.url, name: a.filename || a.name }))}
            initialIndex={lightbox.idx}
            open={lightbox.open}
            onClose={() => setLightbox({ open: false, idx: 0 })}
          />
        </div>
      )}

      {/* Before & after — pair completion photos with the AI marked-up originals */}
      <BeforeAfterCompare entityType={entityType} entityId={id} canEdit />

      {/* Job checklist — operative ticks items as they go */}
      <JobChecklist job={job} onChanged={load} canTick />

      {/* Voice notes — tap, talk, transcribed onto the job */}
      <div className="bg-card border border-border rounded-lg p-5" data-testid="in-house-voice-notes">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Microphone className="h-5 w-5" /> Voice notes <span className="text-xs text-muted-foreground font-normal">({voiceNotes.length})</span></h2>
          <VoiceNoteRecorder entityType={entityType} entityId={id} mode="note" onSaved={(n) => setVoiceNotes((v) => [n, ...v])} />
        </div>
        <p className="text-xs text-muted-foreground mb-2">Hands full? Record what you found and did — it's transcribed and saved on the job for the office.</p>
        {voiceNotes.length === 0 ? <p className="text-sm text-slate-500 text-center py-2">No voice notes yet.</p> : (
          <div className="space-y-2">{voiceNotes.map((n) => (
            <div key={n.id} className="rounded-md border border-border p-3" data-testid={`in-house-voice-note-${n.id}`}>
              <p className="text-sm whitespace-pre-wrap">{n.text}</p>
              <p className="text-[11px] text-slate-500 mt-1">{n.user_name} · {new Date(n.created_at).toLocaleString()}{n.audio_url && <> · <button type="button" onClick={() => openAuthFile(n.audio_url)} className="text-blue-600 hover:underline" data-testid={`in-house-voice-audio-${n.id}`}>Play recording</button></>}</p>
            </div>))}</div>
        )}
      </div>

      {/* Parts used from the van — deducted from stock, costed to the job */}
      {entityType === "job" && !isSupplierSide && <JobPartsPanel jobId={id} />}

      {/* Files & Documents (full hub) — operatives can upload on-site photos here */}
      <div className="bg-card border border-border rounded-lg p-5" data-testid="in-house-files-section">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
          <Paperclip className="h-5 w-5" /> Files & Site Photos
        </h2>
        <p className="text-xs text-muted-foreground mb-3">Upload on-site photos (before/during/after) and any supporting documents here. They're saved against this job.</p>
        <EntityFiles entityType={entityType} entityId={id} currentUser={user} />
      </div>

      {/* Job Sheets — every job type */}
      {forms.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-5" data-testid="in-house-forms-section">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5" /> Job Sheets to Complete
          </h2>
          <div className="space-y-2">
            {forms.map((f) => {
              const submitted = submissions.find(s => s.form_id === f.id);
              return (
                <div key={f.id}
                     className="flex items-center justify-between gap-3 p-3 border border-border rounded-md"
                     data-testid={`in-house-form-row-${f.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{f.title || "Untitled form"}</p>
                    {f.description && <p className="text-xs text-muted-foreground truncate">{f.description}</p>}
                    {submitted && (
                      <p className="text-[11px] text-emerald-600 mt-0.5 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" weight="fill" /> Submitted {new Date(submitted.created_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Button size="sm" variant={submitted ? "outline" : "default"} onClick={() => setFillForm(f)} data-testid={`in-house-form-open-${f.id}`}>
                    {submitted ? "View / Update" : "Fill in"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Expenses (in-house only — supplier staff expense their own employer) */}
      {!isSupplierSide && <div className="bg-card border border-border rounded-lg p-5" data-testid="in-house-expenses-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-semibold">Expenses</h2>
            {!!expenses.length && (
              <p className="text-xs text-slate-500 mt-0.5">Total submitted: <strong>£{expensesTotal.toFixed(2)}</strong></p>
            )}
          </div>
          <Button size="sm" onClick={() => setExpenseDlg({ open: true, amount: "", description: "", category: "materials", file: null })}
                  data-testid="in-house-add-expense-btn">
            <CurrencyGbp className="h-4 w-4 mr-1" /> Submit expense
          </Button>
        </div>

        {expenses.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No expenses submitted yet.</p>
        ) : (
          <div className="space-y-2">
            {expenses.map((x) => (
              <div key={x.id} className="flex items-center gap-3 p-3 rounded-md border border-border" data-testid={`in-house-expense-${x.id}`}>
                <Receipt className="h-5 w-5 text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                    £{Number(x.amount).toFixed(2)} <span className="text-xs text-slate-500 font-normal uppercase tracking-wide">· {x.category}</span>
                  </p>
                  {x.description && <p className="text-sm text-slate-600 dark:text-slate-300 truncate">{x.description}</p>}
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {x.status === "approved" && <span className="text-green-600 font-medium">Approved</span>}
                    {x.status === "rejected" && <span className="text-red-600 font-medium">Rejected</span>}
                    {x.status === "submitted" && <span className="text-amber-600 font-medium">Pending review</span>}
                    {" · "}{new Date(x.submitted_at).toLocaleString()}
                  </p>
                </div>
                {x.receipt_url && (
                  <button
                    type="button"
                    onClick={() => openAuthFile(x.receipt_url)}
                    className="text-xs text-blue-600 hover:underline shrink-0"
                    data-testid={`in-house-view-receipt-${x.id}`}
                  >
                    View receipt
                  </button>
                )}
                {x.status === "submitted" && (
                  <button onClick={() => deleteExpense(x.id)} className="text-red-500 hover:text-red-700 shrink-0" title="Delete" data-testid={`in-house-expense-delete-${x.id}`}>
                    <Trash className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>}

      {/* Complete dialog */}
      <Dialog open={completeDlg.open} onOpenChange={(o) => setCompleteDlg({ ...completeDlg, open: o })}>
        <DialogContent data-testid="in-house-complete-dialog">
          <DialogHeader>
            <DialogTitle>Complete job</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {job.sub_job_photos_optional ? (
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="before-after-optional-note">
                <p className="font-semibold mb-1 text-foreground">Photos optional for this sub-job</p>
                <p>Your company lets sub-jobs be closed without Before/After photos. Add them in Files &amp; Site Photos if you can — they still help the client.</p>
              </div>
            ) : (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 text-xs text-amber-900 dark:text-amber-200" data-testid="before-after-reminder">
                <p className="font-semibold mb-1">Photo evidence required</p>
                <p>Upload at least one photo into the <strong>Before</strong> and <strong>After</strong> folders in the Files &amp; Site Photos section below before marking complete. This protects you (and the client) with visual proof of the work done.</p>
              </div>
            )}
            {checklistStats(job).outstanding > 0 && (
              <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-700 p-3 text-xs text-orange-900 dark:text-orange-200" data-testid="checklist-incomplete-warning">
                <p className="font-semibold mb-1">Checklist not finished</p>
                <p>
                  {checklistStats(job).outstanding} checklist item(s) are still unticked
                  {checklistStats(job).requiredOutstanding ? ` (${checklistStats(job).requiredOutstanding} marked required)` : ""}.
                  You can still complete, but please double-check you haven't missed a step.
                </p>
              </div>
            )}
            {safety.qaReturned && (
              <div className="rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs" data-testid="in-house-complete-qa-ack">
                <p className="font-semibold text-amber-900 dark:text-amber-200 mb-1">AI photo check flagged this job</p>
                <p className="text-amber-800 dark:text-amber-300 mb-2">{safety.photoQa?.summary}</p>
                <label className="flex items-start gap-2 cursor-pointer"><input type="checkbox" checked={qaAck.checked} onChange={(e) => setQaAck({ ...qaAck, checked: e.target.checked })} className="mt-0.5" data-testid="in-house-qa-ack-check" /> I've reviewed the photos — they're correct, complete without another check</label>
                {qaAck.checked && <Input value={qaAck.note} onChange={(e) => setQaAck({ ...qaAck, note: e.target.value })} placeholder="Why the photos are fine (optional, seen by the office)" className="mt-2 h-8 text-xs" data-testid="in-house-qa-ack-note" />}
                {!qaAck.checked && <p className="text-[11px] text-amber-800/80 dark:text-amber-300/80 mt-1">Leave unticked to run the photo check again on your new photos.</p>}
              </div>
            )}
            <div className="flex items-center justify-between"><Label>Completion notes (optional)</Label><VoiceNoteRecorder mode="dictate" size="sm" label="Dictate" onText={(t) => setCompleteDlg((d) => ({ ...d, notes: (d.notes ? d.notes + "\n" : "") + t }))} /></div>
            <Textarea rows={4} value={completeDlg.notes}
                      onChange={(e) => setCompleteDlg({ ...completeDlg, notes: e.target.value })}
                      placeholder="What did you do? Any follow-ups?"
                      data-testid="in-house-complete-notes" />
            <p className="text-xs text-slate-500">Your current location will be captured on submit.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDlg({ ...completeDlg, open: false })}>Cancel</Button>
            <Button onClick={markComplete} disabled={working} data-testid="in-house-complete-confirm">
              {working ? "Completing…" : "Confirm complete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense dialog */}
      <Dialog open={expenseDlg.open} onOpenChange={(o) => setExpenseDlg({ ...expenseDlg, open: o })}>
        <DialogContent data-testid="in-house-expense-dialog">
          <DialogHeader>
            <DialogTitle>Submit expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount (£)</Label>
              <Input type="number" step="0.01" min="0" value={expenseDlg.amount}
                     onChange={(e) => setExpenseDlg({ ...expenseDlg, amount: e.target.value })}
                     placeholder="0.00"
                     autoFocus data-testid="in-house-expense-amount" />
            </div>
            <div>
              <Label>Category</Label>
              <select value={expenseDlg.category}
                      onChange={(e) => setExpenseDlg({ ...expenseDlg, category: e.target.value })}
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      data-testid="in-house-expense-category">
                <option value="materials">Materials</option>
                <option value="fuel">Fuel / mileage</option>
                <option value="tools">Tools / equipment</option>
                <option value="parking">Parking / tolls</option>
                <option value="subsistence">Subsistence</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={expenseDlg.description}
                        onChange={(e) => setExpenseDlg({ ...expenseDlg, description: e.target.value })}
                        placeholder="e.g. 3x copper elbows from Screwfix"
                        data-testid="in-house-expense-description" />
            </div>
            <div>
              <Label>Receipt (image or PDF, optional)</Label>
              <input ref={fileInputRef} type="file" accept="image/*,application/pdf"
                     onChange={(e) => setExpenseDlg({ ...expenseDlg, file: e.target.files?.[0] || null })}
                     className="text-sm"
                     data-testid="in-house-expense-receipt" />
              {expenseDlg.file && (
                <p className="text-xs text-slate-500 mt-1">
                  <Upload className="inline h-3 w-3 mr-1" /> {expenseDlg.file.name} ({(expenseDlg.file.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseDlg({ ...expenseDlg, open: false })}>Cancel</Button>
            <Button onClick={submitExpense} disabled={working} data-testid="in-house-expense-submit">
              {working ? "Submitting…" : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job sheet fill dialog (operatives can fill assigned forms inline) */}
      <JobFormFillDialog
        jobId={id}
        form={fillForm}
        open={!!fillForm}
        onClose={() => setFillForm(null)}
        onSubmitted={load}
      />
      </div>
    </div>
  );
}
